import React, { useState } from 'react';
import {
  Target,
  BookOpen,
  X,
  ChevronRight,
  Sparkles,
  GraduationCap,
  ChevronLeft,
  Menu,
  Lightbulb,
  LineChart
} from 'lucide-react';

const AladinnNavigation = ({ user, onNavigate, currentSection, currentCompetence, onLogout }) => {
  const [menuCollapsed, setMenuCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const menuSections = [
    {
      id: 'objectif',
      title: 'Mon Ambition',
      icon: <Target className="w-6 h-6" />,
      defaultSubsection: 'competences',
      subsections: [
        { 
          id: 'competences',
          title: 'Mes objectifs',
          description: 'Liste de mes objectifs'
        },
        { 
          id: 'details',
          title: 'Recommandations',
          description: 'Vision détaillée'
        }
      ]
    },
    {
      id: 'apprentissage',
      title: 'Apprentissage',
      icon: <BookOpen className="w-6 h-6" />,
      defaultSubsection: 'cours',
      subsections: [
        { 
          id: 'cours',
          title: 'Concepts clés',
          description: 'Contenus pédagogiques'
        },
        { 
          id: 'uploadDocument',
          title: 'Mes Documents',
          description: 'Gérer mes ressources'
        }
      ]
    },
    {
      id: 'inspiration',
      title: 'Inspiration',
      icon: <Lightbulb className="w-6 h-6" />,
      defaultSubsection: 'recherche',
      subsections: [
        {
          id: 'recherche',
          title: 'Recherche',
          description: 'Un monde d\'opportunités'
        },
        {
          id: 'conseils',
          title: 'Playing game',
          description: 'Have fun and learn'
        }
      ]
    },
    {
      id: 'entrainement',
      title: 'Entraînement',
      icon: <GraduationCap className="w-6 h-6" />,
      defaultSubsection: 'training',
      subsections: [
        { 
          id: 'training',
          title: 'Exercices personnalisés',
          description: 'Pratiquez et progressez'
        },
        { 
          id: 'quiz',
          title: 'Quiz',
          description: 'Testez vos connaissances'
        }
      ]
    },
    {
      id: 'tableau-de-bord',
      title: 'Tableau de Bord',
      icon: <LineChart className="w-6 h-6" />,
      defaultSubsection: 'progression',
      subsections: [
        { 
          id: 'progression',
          title: 'Ma Progression',
          description: 'Suivi des progrès'
        },
        { 
          id: 'competences-acquises',
          title: 'Compétences Acquises',
          description: 'Badges et certifications'
        }
      ]
    }
  ];

  const handleNavigation = (sectionId) => {
    onNavigate(sectionId);
    setMobileMenuOpen(false);
  };

  const handleCollapsedSectionClick = (section) => {
    if (menuCollapsed) {
      handleNavigation(section.defaultSubsection);
    }
  };

  const toggleMenu = () => {
    setMenuCollapsed(!menuCollapsed);
  };

  // Nouvelle fonction pour trouver la section parente d'une sous-section
  const findParentSection = (subsectionId) => {
    return menuSections.find(section => 
      section.subsections.some(subsection => subsection.id === subsectionId)
    );
  };

  const isSectionActive = (sectionId) => {
    const currentParentSection = findParentSection(currentSection);
    return currentParentSection?.id === sectionId || currentSection === sectionId;
  };

  return (
    <>
      {/* Bouton mobile */}
      <button
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-white rounded-lg shadow-lg hover:bg-gray-100"
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
      >
        {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>

      {/* Overlay mobile */}
      {mobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Menu principal */}
      <div 
        className={`fixed top-0 left-0 h-screen bg-white shadow-xl z-40
                   transition-all duration-300 ease-in-out
                   ${menuCollapsed ? 'w-16' : 'w-64'}
                   ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}
      >
        {/* En-tête */}
        <div className="h-16 border-b border-gray-200 flex items-center justify-between px-4">
          <div className={`flex items-center ${menuCollapsed ? 'justify-center w-full' : ''}`}>
            <Sparkles className="w-8 h-8 text-purple-600" /> 
            {!menuCollapsed && <span className="ml-3 font-bold text-xl text-purple-600">Aladinn</span>}
          </div>
          
          <button
            onClick={toggleMenu}
            className="hidden lg:flex items-center justify-center w-8 h-8 rounded-full hover:bg-gray-100"
          >
            {menuCollapsed ? (
              <ChevronRight className="w-5 h-5 text-gray-600" />
            ) : (
              <ChevronLeft className="w-5 h-5 text-gray-600" />
            )}
          </button>
        </div>

        {/* Zone de scroll */}
        <div className="h-[calc(100vh-4rem)] overflow-y-auto">
          {/* Info utilisateur */}
          {user && !menuCollapsed && (
            <div className="p-4 border-b border-gray-200">
              <p className="text-sm text-gray-600 truncate">
                {user.displayName || user.email}
              </p>
              <button
                onClick={onLogout}
                className="mt-2 text-sm text-red-600 hover:text-red-700"
              >
                Se déconnecter
              </button>
            </div>
          )}

          {/* Navigation */}
          <div className="p-2">
            {menuSections.map((section) => (
              <div key={section.id} className="mb-4">
                <button
                  onClick={() => handleCollapsedSectionClick(section)}
                  className={`w-full flex items-center p-2 rounded-lg
                            transition-colors duration-200
                            ${menuCollapsed ? 'justify-center' : ''}
                            ${isSectionActive(section.id)
                              ? 'bg-purple-100 text-purple-700'
                              : 'text-gray-600 hover:bg-purple-50 hover:text-purple-700'}`}
                >
                  <div className="flex-shrink-0 relative group">
                    {section.icon}
                    {menuCollapsed && (
                      <div className="absolute left-full ml-2 hidden group-hover:block 
                                    bg-white shadow-lg rounded-md p-2 z-50 whitespace-nowrap">
                        <span className="text-sm">{section.title}</span>
                      </div>
                    )}
                  </div>
                  {!menuCollapsed && <span className="ml-2 font-medium">{section.title}</span>}
                </button>
                
                {/* Sous-sections */}
                {!menuCollapsed && (
                  <ul className="ml-4 mt-1 space-y-1">
                    {section.subsections.map((subsection) => (
                      <li key={subsection.id}>
                        <button
                          onClick={() => handleNavigation(subsection.id)}
                          className={`w-full text-left p-2 rounded-lg flex items-center text-sm
                                    transition-colors duration-200
                                    ${currentSection === subsection.id
                                      ? 'bg-purple-100 text-purple-700' 
                                      : 'text-gray-600 hover:bg-purple-50 hover:text-purple-700'}`}
                        >
                          <ChevronRight className={`w-4 h-4 mr-2 ${currentSection === subsection.id
                            ? 'text-purple-700' 
                            : 'text-gray-400'}`} />
                          <div>
                            <p className="font-medium">{subsection.title}</p>
                            <p className="text-xs text-gray-500">{subsection.description}</p>
                          </div>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default AladinnNavigation;